// External Imports
import { useContext, useEffect, useRef, useState } from "react";
import { add, format } from "date-fns";
import { MdClose } from "react-icons/md";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import DetailPair from "../UI/DetailPair";
import CustomerSelector from "../customers-page/CustomerSelector";
import Heading from "../UI/Heading";
import VehicleSelector from "../vehicles-page/VehicleSelector";
import SelectPair from "../UI/SelectPair";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import { UtilityContext } from "../../context/util-context";
import { AuthContext } from "../../context/auth-context";
import { toTitleCase } from "../../util/format-text";
import { useLocation } from "react-router-dom";

/** ========================================================================
 * * AddQuote Component
 * This component renders a form to send a quote to a lead.
 * ========================================================================== */

export default function AddQuote() {
  // Load Context
  const { navigate } = useContext(UtilityContext);

  // Load Params
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [customer, setCustomer] = useState();
  const [vehicles, setVehicles] = useState([]);

  // Refs
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const listRef = useRef(null);

  /* ====================== FETCH PLANS ====================== */
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch-all", {
        table: "plan",
      });
      setPlans(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== SELECT CUSTOMER ====================== */
  const selectCustomer = (customer) => {
    nameRef.current.value = `${customer.firstName} ${customer.lastName}`;
    emailRef.current.value = customer.email;
    phoneRef.current.value = customer.phone;
    setCustomer(customer);
  };

  /* ====================== SEND QUOTE ====================== */
  const submitHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        name: toTitleCase(nameRef.current.value),
        email: emailRef.current.value,
        phone: phoneRef.current.value,
        expDate: document.getElementById("expDate").value,
        vehicles: [],
      };

      // Add Vehicles to Payload
      vehicles.forEach((vehicle) => {
        const entry = {
          id: vehicle.id,
          make: vehicle.make.name,
          model: vehicle.model,
          year: vehicle.year,
          trim: vehicle.trim,
          cornerImage: vehicle.cornerImage,
          plan: vehicle.planId,
          rate: Number(
            document.getElementById(`rate-${vehicle.id}`).value
          ).toFixed(2),
          period: document.getElementById(`period-${vehicle.id}`).value,
          setup: Number(
            document.getElementById(`setup-${vehicle.id}`).value
          ).toFixed(2),
          trial: document.getElementById(`trial-${vehicle.id}`).value === "Yes",
          miles: Number(
            document.getElementById(`miles-${vehicle.id}`).value
          ).toFixed(0),
          mileage: Number(
            document.getElementById(`mileage-${vehicle.id}`).value
          ).toFixed(2),
          insurance:
            document.getElementById(`insurance-${vehicle.id}`).value ===
            "Included",
          maintenance:
            document.getElementById(`maintenance-${vehicle.id}`).value ===
            "Included",
        };
        payload.vehicles.push(entry);
      });

      // Send Request
      const url = await requestAdminAPI("send-quote", payload);
      window.open(url, "_blank");
      navigate("/quotes");

      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch Plans on Mount
  useEffect(() => {
    fetchPlans();
  }, []);

  // Select Fields from URL Params
  useEffect(() => {
    nameRef.current.value = params.get("name") || "";
    emailRef.current.value = params.get("email") || "";
    phoneRef.current.value = params.get("phone") || "";
  }, []);

  // Scroll List to Bottom
  useEffect(() => {
    if (listRef.current)
      listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [vehicles]);

  return (
    <div className="container lg:gap-8">
      <Heading
        title="Add Quote"
        text="Quotes can be sent to existing customers or prospective customers by providing information manually. If a phone number is provided they will also receive an SMS with a link to a PDF version of the quote. "
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <CustomerSelector selected={customer?.id} onSelect={selectCustomer} />
        <div className="info lg:grid-cols-3">
          <DetailPair
            label="Name"
            edit={true}
            inputOptions={{
              id: "name",
              type: "text",
              ref: nameRef,
            }}
          />
          <DetailPair
            label="Email"
            edit={true}
            inputOptions={{
              id: "email",
              type: "email",
              ref: emailRef,
            }}
          />
          <DetailPair
            label="Phone"
            edit={true}
            inputOptions={{
              id: "phone",
              type: "tel",
              ref: phoneRef,
            }}
          />
        </div>
      </div>

      <div className="list" ref={listRef}>
        {vehicles.map((vehicle, idx) => (
          <VehicleCard
            key={idx}
            vehicle={vehicle}
            onRemove={(vehicle) =>
              setVehicles(vehicles.filter((v) => v.id !== vehicle.id))
            }
            plans={plans}
          />
        ))}
      </div>

      {loading ? (
        <LoadingSpinnerWrapper />
      ) : (
        <div className="info">
          <div className="lg:col-span-3">
            <VehicleSelector
              label="ADD VEHICLE"
              selected={false}
              onSelect={(v) => setVehicles([...vehicles, v])}
            />
          </div>
          <DetailPair
            label="Expiration Date"
            edit={true}
            inputOptions={{
              id: "expDate",
              type: "date",
              defaultValue: format(add(new Date(), { weeks: 1 }), "yyyy-MM-dd"),
            }}
          />
          <div className="btn-primary" onClick={submitHandler}>
            Send Quote
          </div>
        </div>
      )}
    </div>
  );
}

/** ========================================================================
 * * VehicleCard Component
 * ========================================================================== */

const VehicleCard = ({ vehicle, onRemove, plans }) => {
  return (
    <div className="grid items-center gap-4 p-2 lg:grid-cols-4">
      <div
        className="clickable"
        onClick={() => window.open(`/vehicles/info?id=${vehicle.id}`, "_blank")}
      >
        <img src={vehicle.cornerImage} className="object-contain" />
      </div>
      <div className="grid content-start gap-2 lg:col-span-3">
        <div className="flex items-center justify-between space-x-4">
          <p className="text-lg font-bold">
            {vehicle.year} {vehicle.model} {vehicle.vin.substring(13, 17)}{" "}
            <span className="text-sm text-stone-400">({vehicle.trim})</span>
          </p>
          <MdClose className="icon" onClick={() => onRemove(vehicle)} />
        </div>

        <hr className="hr" />
        <div className="info">
          <DetailPair
            label="Rate"
            edit={true}
            inputOptions={{
              id: `rate-${vehicle.id}`,
              type: "number",
              step: "0.01",
              defaultValue: vehicle.rate,
            }}
          />
          <SelectPair
            label="Period"
            options={[
              { id: "month", name: "Monthly" },
              { id: "bi-weekly", name: "Bi-Weekly" },
              { id: "week", name: "Weekly" },
              { id: "day", name: "Daily" },
            ]}
            edit={true}
            inputOptions={{ id: `period-${vehicle.id}` }}
          />
          <DetailPair
            label="Set Up Fee"
            edit={true}
            inputOptions={{
              id: `setup-${vehicle.id}`,
              type: "number",
              step: "0.01",
              defaultValue: plans.find((p) => p.id === vehicle.planId)?.price,
            }}
          />
          <SelectPair
            label="Trial"
            options={["Yes", "No"]}
            edit={true}
            inputOptions={{ id: `trial-${vehicle.id}` }}
          />
          <DetailPair
            label="Miles Per Period"
            edit={true}
            inputOptions={{
              id: `miles-${vehicle.id}`,
              type: "number",
              defaultValue: vehicle.monthlyMiles,
            }}
          />
          <DetailPair
            label="Extra Mileage Rate"
            edit={true}
            inputOptions={{
              id: `mileage-${vehicle.id}`,
              type: "number",
              step: "0.01",
              defaultValue: (vehicle.monthlyMiles / vehicle.rate).toFixed(2),
            }}
          />
          <SelectPair
            label="Insurance"
            options={["Included", "Not Included"]}
            edit={true}
            inputOptions={{ id: `insurance-${vehicle.id}` }}
          />
          <SelectPair
            label="Maintenance"
            options={["Included", "Not Included"]}
            edit={true}
            inputOptions={{ id: `maintenance-${vehicle.id}` }}
          />
        </div>
      </div>
    </div>
  );
};
